import React, { useEffect, useMemo, useState } from "react";
import bkmxTopics from "../constants/bkmxTopics.const";
import oldaTopics from "../constants/oldaTopics.const";
import Plot from "react-plotly.js";
import * as Plotly from "plotly.js";
import { getData } from "../helpers/TopicDetailPageHelpers";

interface TopicsProps {
  nextTheme: string;
}

const Topics: React.FC<TopicsProps> = ({ nextTheme }) => {
  const [query, setQuery] = useState("");
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [plotData, setPlotData] = useState<Plotly.Data[]>([]);
  const [allBkmxData, setAllBkmxData] = useState<Record<string, any> | null>(
    null
  );
  const [allOldaData, setAllOldaData] = useState<Record<string, any> | null>(
    null
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [activeModel, setActiveModel] = useState("BKMX");

  const modelOptions = ["BKMX", "oLDA"];

  useEffect(() => {
    fetch("/bkmx_quarterly.json")
      .then((res) => res.json())
      .then((data) => {
        setAllBkmxData(data);
      })
      .catch((err) => {
        console.error("Failed to load bkmx data:", err);
      });

    fetch("/olda_quarterly.json")
      .then((res) => res.json())
      .then((data) => {
        setAllOldaData(data);
      })
      .catch((err) => {
        console.error("Failed to load olda data:", err);
      });
  }, []);

  const filteredTopics = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (activeModel === "BKMX") {
      if (!q) return bkmxTopics;
      return bkmxTopics.filter((item) => item.toLowerCase().includes(q));
    } else {
      if (!q) return oldaTopics;
      return oldaTopics.filter((item) => item.toLowerCase().includes(q));
    }
  }, [query, activeModel]);

  const handleCheckboxChange = (name: string) => {
    setSelectedTopics((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    );
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleFetchData = async () => {
    if (!allBkmxData || !allOldaData) {
      return;
    }

    const selectedBkmxData: { dates: any; [key: string]: any } = getData(
      selectedTopics,
      allBkmxData
    );
    const selectedOldaData: { dates: any; [key: string]: any } = getData(
      selectedTopics,
      allOldaData
    );

    const plot: Plotly.Data[] = [];

    for (const topic of selectedTopics) {
      if (selectedBkmxData[topic]) {
        plot.push({
          x: selectedBkmxData.dates,
          y: selectedBkmxData[topic],
          type: "scatter",
          mode: "lines+markers",
          name: `${topic} (BKMX)`,
        } as Plotly.Data);
      }

      if (selectedOldaData[topic]) {
        plot.push({
          x: selectedOldaData.dates,
          y: selectedOldaData[topic],
          type: "scatter",
          mode: "lines+markers",
          name: `${topic} (oLDA)`,
        } as Plotly.Data);
      }
    }
    setPlotData(plot);
    toggleDropdown();
  };

  const handleClearSelection = () => {
    setPlotData([]);
    setSelectedTopics([]);
  };

  const handleDownload = () => {
    const files = ["/bkmx_topics.csv", "/olda_topics.csv"];

    files.forEach((file) => {
      const link = document.createElement("a");
      link.href = file;
      link.download = file.split("/").pop() || file;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-900">
      <div className="max-w-4xl mx-auto mt-2 text-center text-gray-600 dark:text-slate-100">
        <p>
          Select multiple topics using the checkboxes below and click{" "}
          <strong>“Plot Selected Topics”</strong> to see how attention to topics
          changes over time.{" "}
          <span
            onClick={handleDownload}
            className="text-blue-600 underline cursor-pointer"
          >
            Download the datasets
          </span>{" "}
          used in this analysis.
        </p>
      </div>

      <div className="max-w-5xl mx-auto mt-10 bg-white dark:bg-slate-900 rounded-lg shadow p-6 pb-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold dark:text-slate-100">
            News Topics
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handleFetchData}
              disabled={selectedTopics.length === 0}
              className={`px-4 py-2 rounded text-white ${
                selectedTopics.length === 0
                  ? "bg-gray-400 dark:bg-slate-600 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              Plot{" "}
              {selectedTopics.length === 0 ? "Selected" : selectedTopics.length}{" "}
              {selectedTopics.length > 1 ? "Topics" : "Topic"}
            </button>

            <select
              value={activeModel}
              onChange={(e) => setActiveModel(e.target.value)}
              className="border rounded px-3 py-2 bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-slate-100"
            >
              {modelOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>

            <button
              onClick={handleClearSelection}
              disabled={selectedTopics.length === 0}
              className={`px-4 py-2 rounded text-white ${
                selectedTopics.length === 0
                  ? "bg-gray-400 dark:bg-slate-600 cursor-not-allowed"
                  : "bg-red-500 hover:bg-red-600"
              }`}
            >
              Clear All
            </button>

            <button
              onClick={toggleDropdown}
              className="px-4 py-2 rounded text-white bg-gray-600 hover:bg-gray-700"
            >
              {isDropdownOpen ? "▲" : "▼"}
            </button>
          </div>
        </div>

        {isDropdownOpen && (
          <>
            <input
              type="text"
              placeholder="Search for topics..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full dark:bg-slate-800 dark:text-white border border-gray-300 dark:border-slate-700 rounded px-3 py-2 mb-6 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <div className="grid grid-cols-4 gap-3">
              {filteredTopics.map((topic: string) => (
                <label
                  key={topic}
                  className="flex items-center border dark:border-slate-600 ounded px-3 py-2 text-sm hover:bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800 cursor-pointer dark:text-slate-100"
                >
                  <input
                    id={topic}
                    type="checkbox"
                    value={topic}
                    checked={selectedTopics.includes(topic)}
                    onChange={() => handleCheckboxChange(topic)}
                    className="mr-2"
                  />
                  {topic}
                </label>
              ))}
            </div>
          </>
        )}
      </div>

      {plotData.length > 0 && (
        <div className="max-w-5xl mx-auto mt-10">
          <Plot
            data={plotData}
            layout={{
              title: {
                text: "Attention Proportion vs. Date",
                font: { color: nextTheme === "dark" ? "#000000ff" : "#f1f5f9" },
              },
              xaxis: {
                title: {
                  text: "Date",
                  font: {
                    color: nextTheme === "dark" ? "#000000ff" : "#f1f5f9",
                  },
                },
              },
              yaxis: {
                title: {
                  text: "Attention Proportion",
                  font: {
                    color: nextTheme === "dark" ? "#000000ff" : "#f1f5f9",
                  },
                },
              },
              paper_bgcolor: "rgba(0,0,0,0)",
              plot_bgcolor: "rgba(0,0,0,0)",
              font: { color: nextTheme === "dark" ? "#000000ff" : "#f1f5f9" },
              width: 900,
              height: 500,
              autosize: true,
            }}
            config={{ responsive: true }}
          />
        </div>
      )}
    </div>
  );
};

export default Topics;
